import { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { usePortfolio } from '../../context/PortfolioContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function MonthlyIncomeChart() {
  const { dividends } = usePortfolio();

  const { chartData, total, monthlyAvg } = useMemo(() => {
    if (!dividends?.length) return { chartData: null, total: 0, monthlyAvg: 0 };

    // Calculate monthly income for last 12 months
    const now = new Date();
    const monthlyData = new Array(12).fill(0);
    const labels = [];

    // Generate labels for last 12 months
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthIndex = d.getMonth();
      const year = d.getFullYear();
      labels.push(`${MONTHS[monthIndex]} ${year.toString().slice(2)}`);
    }

    // Aggregate dividends by month
    dividends.forEach(dividend => {
      const divDate = new Date(dividend.date);
      const amount = Number(dividend.amount_gbp || 0);
      
      // Check if this dividend falls within last 12 months
      const monthsAgo = (now.getFullYear() - divDate.getFullYear()) * 12 + 
                        (now.getMonth() - divDate.getMonth());
      
      if (monthsAgo >= 0 && monthsAgo < 12) {
        const index = 11 - monthsAgo;
        if (index >= 0 && index < 12) {
          monthlyData[index] += amount;
        }
      }
    });

    const total = monthlyData.reduce((a, b) => a + b, 0);
    const monthlyAvg = total / 12;

    return {
      chartData: {
        labels,
        datasets: [{
          label: 'Dividend Income (GBP)',
          data: monthlyData,
          backgroundColor: (ctx) => {
            const value = ctx.raw;
            // Gradient-like colors based on value
            if (value > 50) return 'rgba(59, 130, 246, 0.8)'; // Blue-500
            if (value > 20) return 'rgba(14, 165, 233, 0.8)';  // Sky-500
            return 'rgba(6, 182, 212, 0.8)';                    // Cyan-500
          },
          borderColor: 'transparent',
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: false,
        }],
      },
      total,
      monthlyAvg,
    };
  }, [dividends]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(12, 28, 48, 0.96)',
        borderColor: 'rgba(0, 212, 255, 0.35)',
        borderWidth: 1,
        titleColor: '#dff0ff',
        bodyColor: '#dff0ff',
        padding: 10,
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            return new Intl.NumberFormat('en-GB', {
              style: 'currency',
              currency: 'GBP',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(value);
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#7a99b8',
          font: { family: 'Space Mono', size: 9 },
        },
      },
      y: {
        grid: {
          color: 'rgba(29, 56, 85, 0.25)',
          drawBorder: false,
        },
        ticks: {
          color: '#7a99b8',
          font: { family: 'Space Mono', size: 10 },
          callback: (value) => {
            return new Intl.NumberFormat('en-GB', {
              style: 'currency',
              currency: 'GBP',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(value);
          },
        },
      },
    },
  };

  if (!chartData) {
    return (
      <div className="chart-container" style={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span className="text-muted">No dividend data available</span>
      </div>
    );
  }

  return (
    <div className="chart-container" style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <div style={{ fontSize: '0.75rem', color: '#dff0ff', fontWeight: 600 }}>Monthly Income (12M)</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#dff0ff' }}>
            {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0 }).format(total)}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="summary-card-label" style={{ fontSize: '0.75rem', color: '#7a99b8' }}>Average Monthly</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 600, color: '#00d4ff' }}>
            {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0 }).format(monthlyAvg)}
          </div>
        </div>
      </div>
      <div style={{ height: '200px' }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
}

export default MonthlyIncomeChart;
